import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';
import { Button } from '../../components/ui/button';

const TrademarkRegistration = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pricingPlans = [
    {
      name: "Basic",
      price: "$99",
      features: [
        "Trademark Search",
        "Filing Assistance",
        "USPTO Application",
        "Email Support",
        "Standard Processing"
      ]
    },
    {
      name: "Standard",
      price: "$249",
      popular: true,
      features: [
        "Everything in Basic",
        "Comprehensive Search",
        "Attorney Review",
        "Priority Support",
        "Office Action Response",
        "Faster Processing"
      ]
    },
    {
      name: "Premium",
      price: "$499",
      features: [
        "Everything in Standard",
        "Dedicated Attorney",
        "24/7 Premium Support",
        "Multi-class Filing",
        "International Trademark",
        "Lifetime Monitoring"
      ]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Free Consultation",
      description: "Discuss your trademark needs with our expert attorneys and get personalized guidance."
    },
    {
      step: "02",
      title: "Filing & Submission",
      description: "We prepare and file your trademark application with the USPTO following all legal requirements."
    },
    {
      step: "03",
      title: "Approval & Registration",
      description: "Track your application status and receive your official trademark registration certificate."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "E-commerce Business Owner",
      text: "The trademark registration process was smooth and professional. Highly recommend their services!"
    },
    {
      name: "Michael Chen",
      role: "Tech Startup Founder",
      text: "Fast, efficient, and affordable. Got my trademark registered in record time."
    },
    {
      name: "Emily Rodriguez",
      role: "Fashion Designer",
      text: "Excellent customer service and expert legal guidance throughout the entire process."
    },
    {
      name: "David Thompson",
      role: "Restaurant Owner",
      text: "Protected my brand identity with ease. The team was incredibly helpful and responsive."
    },
    {
      name: "Lisa Wang",
      role: "App Developer",
      text: "Professional trademark attorneys who truly understand business needs. Five stars!"
    }
  ];

  const faqs = [
    {
      question: "How long does trademark registration take?",
      answer: "Trademark registration typically takes 8-12 months from filing to approval, depending on USPTO processing times and any office actions."
    },
    {
      question: "What can be trademarked?",
      answer: "You can trademark business names, logos, slogans, product names, service marks, and any distinctive brand identifier that represents your business."
    },
    {
      question: "Do I need a lawyer to register a trademark?",
      answer: "While not required, having an attorney significantly increases your chances of approval and helps avoid costly mistakes during the application process."
    },
    {
      question: "What's the difference between ™ and ®?",
      answer: "™ indicates an unregistered trademark claim, while ® can only be used after your trademark is officially registered with the USPTO."
    },
    {
      question: "How long does trademark protection last?",
      answer: "A registered trademark lasts 10 years and can be renewed indefinitely as long as you continue using the mark in commerce."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <Header />
      
      {/* Hero Banner */}
      <div className="pt-20 pb-16 bg-gradient-to-b from-[#131921] to-[#0F1419]">
        <div className="container mx-auto px-6 text-center">
          <Link to="/" className="inline-flex items-center text-[#FF9900] hover:text-orange-400 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-5xl font-bold mb-6 text-[#FF9900]">US Trademark Registration</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Protect your brand with professional trademark registration services. Get federal trademark protection starting at just $99.
          </p>
        </div>
      </div>

      {/* About Service */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
              <img 
                src="https://images.unsplash.com/photo-1707904633074-20fb8f8f865a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjB3b3JrJTIwcHJvZmVzc2lvbmFsJTIwYnVzaW5lc3MlMjBpbGx1c3RyYXRpb24lMjB2ZWN0b3J8ZW58MHx8fHwxNzc1MDcyNTEwfDA&ixlib=rb-4.1.0&q=85"
                alt="Trademark Registration"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">About Trademark Registration</h2>
              <p className="text-gray-300 mb-4 leading-relaxed">
                A trademark is a unique identifier that distinguishes your business, products, or services from competitors. Registering your trademark with the USPTO provides nationwide legal protection and exclusive rights to use your mark in commerce.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Our expert trademark attorneys handle the entire registration process, from comprehensive trademark search to final approval, ensuring your brand is fully protected under federal law.
              </p>
              <Button 
                onClick={() => setIsContactFormOpen(true)}
                className="bg-[#FF9900] hover:bg-orange-600 text-white"
              >
                Get Started Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Simple Process */}
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Our Simple 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="bg-[#0F1419] p-8 rounded-2xl border border-white/10 hover:border-[#FF9900]/50 transition-all">
                <div className="text-6xl font-bold text-[#FF9900] mb-4 opacity-20">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Affordable Pricing Plans</h2>
          <p className="text-center text-gray-300 mb-16">Choose the plan that best fits your business needs</p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className={`relative bg-[#131921] p-8 rounded-2xl border-2 ${
                  plan.popular ? 'border-[#FF9900]' : 'border-white/10'
                } hover:border-[#FF9900]/50 transition-all`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#FF9900] text-white px-4 py-1 rounded-full text-sm font-bold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="text-5xl font-bold text-[#FF9900] mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => setIsContactFormOpen(true)}
                  className={`w-full ${
                  plan.popular 
                    ? 'bg-[#FF9900] hover:bg-orange-600' 
                    : 'bg-white/10 hover:bg-white/20'
                } text-white`}>
                  Choose {plan.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Our Trademark Services?</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Expert Legal Team</h3>
                    <p className="text-gray-300">Experienced trademark attorneys with 100+ successful registrations</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Affordable Pricing</h3>
                    <p className="text-gray-300">Transparent pricing with no hidden fees or surprise charges</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Fast Processing</h3>
                    <p className="text-gray-300">Quick turnaround times and priority filing services available</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Comprehensive Support</h3>
                    <p className="text-gray-300">Dedicated support team available throughout the entire process</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden">
              <img 
                src="/section4.png"
                alt="Why Choose Us"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF9900] to-orange-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Protect Your Brand?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied clients who have successfully registered their trademarks with our expert team.
          </p>
          <Button 
            onClick={() => setIsContactFormOpen(true)}
            className="bg-white text-[#FF9900] hover:bg-gray-100 text-lg px-8 py-6"
          >
            Start Your Trademark Registration
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Testimonials */}
      <ServiceTestimonials 
        testimonials={testimonials}
        serviceName="Trademark Registration"
      />

      {/* FAQs */}
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-[#0F1419] p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold mb-3 text-[#FF9900]">{faq.question}</h3>
                <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactFormDialog 
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </div>
  );
};

export default TrademarkRegistration;
